"use client";

import { useEffect } from "react";
import type { AwsRum, AwsRumConfig } from "aws-rum-web";

declare global {
  interface Window {
    __bouncepointRum?: AwsRum;
  }
}

type RumConfigResponse =
  | {
      enabled: false;
    }
  | {
      enabled: true;
      applicationId: string;
      applicationVersion: string;
      identityPoolId: string;
      guestRoleArn: string;
      region: string;
      endpoint: string;
    };

export default function CloudWatchRum() {
  useEffect(() => {
    if (window.__bouncepointRum) {
      return;
    }

    if (typeof fetch !== "function") {
      return;
    }

    let isCancelled = false;

    void fetch("/api/rum-config", { cache: "no-store" })
      .then((response) => response.json() as Promise<RumConfigResponse>)
      .then(async (rumConfig) => {
        if (!rumConfig.enabled) {
          return;
        }

        const { AwsRum } = await import("aws-rum-web");

        if (isCancelled || window.__bouncepointRum) {
          return;
        }

        const config: AwsRumConfig = {
          sessionSampleRate: 1,
          identityPoolId: rumConfig.identityPoolId,
          guestRoleArn: rumConfig.guestRoleArn,
          endpoint: rumConfig.endpoint,
          telemetries: ["performance", "errors", "http"],
          allowCookies: true,
          enableXRay: false,
        };

        window.__bouncepointRum = new AwsRum(
          rumConfig.applicationId,
          rumConfig.applicationVersion,
          rumConfig.region,
          config,
        );
      })
      .catch(() => {
        // RUM should never break the application if monitoring fails to load.
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  return null;
}
